'use client'
import { useState, useEffect } from 'react';
import styles from './CookieConsent.module.css';
import { useIsClient } from '@/hooks/useBrowser';
import { usePathname } from 'next/navigation';




interface CookieConsentProps {
  onAccept?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(true);
  const isClient = useIsClient();
  const pathname = usePathname();
  const isLandingPage = pathname === '/';


  useEffect(() => {
    if (!isClient) return;

    try {
      // Check if the user has already made a choice
      const consentStatus = localStorage.getItem('cookieConsent');
      const analyticsConsent = localStorage.getItem('analyticsConsent');
      
      if (analyticsConsent === 'true') {
        setAnalyticsEnabled(true);
        // Trigger loading of Google Analytics
        onAccept && onAccept();
      } else if (analyticsConsent === 'false') {
        setAnalyticsEnabled(false);
      }
      
      // If no consent choice stored, show the banner (after a slight delay for better UX)
      if (!consentStatus) {
        const timer = setTimeout(() => {
          setVisible(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
  }, [onAccept,isClient]);

  const handleAcceptAll = (): void => {
    if (!isClient) return;

    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('analyticsConsent', 'true');
    setVisible(false);
    setShowPreferences(false);
    onAccept && onAccept();
  };

  const handleSavePreferences = (): void => {
    if (!isClient) return;

    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('analyticsConsent', analyticsEnabled.toString());
    setVisible(false);
    setShowPreferences(false);
    
    if (analyticsEnabled) {
      onAccept && onAccept();
    }
  };

  // Only hide component if both visible and showPreferences are false
  if (!visible && !showPreferences) return null;

  return (
    <>
      {/* Always show the banner when visible is true */}
      {visible && (
        <div className={`${styles.banner} ${isLandingPage ? styles.landingPageOffset : ''}`}>
          <div className={styles.container}>
            <span className={styles.text}>
              We use&nbsp;<a className="underline" href='/cookie-policy'>cookies.</a>
            </span>
            <div className={styles.buttons}>
              <button 
                onClick={handleAcceptAll}
                className={`${styles.button}`}
              >
                Accept all
              </button>
              <button 
                onClick={() => setShowPreferences(true)}
                className={`${styles.button} ${styles.preferencesbutton} ${showPreferences ? styles.preferencesActive : ''}`}
                >
                Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show preferences modal when showPreferences is true */}
      {showPreferences && (
        <div className={styles.modalOverlay}>
          
          <div className={`${styles.modal} ${isLandingPage ? styles.landingPageModalOffset : ''}`}>

            <h3 className={styles.modalTitle}>Cookie Preferences</h3>
            <div className={styles.optionsContainer}>
              <div className={styles.option}>
                <div className={styles.optionHeader}>
                  <label htmlFor="analytics-cookies">Google Analytics Cookies</label>
                  <div className={styles.customCheckbox}>
                    <input
                      type="checkbox"
                      id="analytics-cookies"
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    />
                    <span className={styles.checkmark}></span>
                  </div>
                </div>
                <p className={styles.optionDescription}>
                  These cookies allow us to count visits and traffic sources to measure and 
                  improve the performance of our site.
                </p>
              </div>
            </div>
            <div className={styles.modalButtons}>
              <button
                onClick={handleSavePreferences}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Confirm and Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;